/**
 * Story store using Zustand.
 * Manages story feed, viewing, and posting.
 */
import { create } from 'zustand';
import type {
  Story,
  UserStoryGroup,
  PostStoryData,
  StoryViewer,
  StoryReaction,
} from '../types';
import { api } from '../api/client';
import { serializeOverlays } from '../utils/overlayPayload';
import { computeUploadTimeoutMs } from '../services/uploadPolicy';

interface StoryState {
  // State
  feed: UserStoryGroup[];
  myStories: Story[];
  isLoading: boolean;
  isPosting: boolean;
  uploadProgress: number;
  error: string | null;

  // Actions - Fetching
  fetchFeed: () => Promise<void>;
  fetchMyStories: () => Promise<void>;
  fetchUserStories: (userId: number) => Promise<Story[]>;

  // Actions - Viewing
  recordView: (storyId: number, ownerUserId: number) => void;
  getStoryViewers: (storyId: number) => Promise<StoryViewer[]>;
  getStoryReactions: (storyId: number) => Promise<StoryReaction[]>;

  // Actions - Posting
  postStory: (data: PostStoryData) => Promise<boolean>;
  deleteStory: (storyId: number) => Promise<boolean>;

  // Actions - Interactions
  reactToStory: (storyId: number, emoji: string) => Promise<boolean>;
  removeReaction: (storyId: number) => Promise<boolean>;
  replyToStory: (storyId: number, content: string) => Promise<boolean>;
  saveToVault: (storyId: number) => Promise<boolean>;

  // Actions - UI
  markStoryViewed: (storyId: number, userId: number) => void;
  clearError: () => void;
}

export const useStoryStore = create<StoryState>((set, get) => ({
  // Initial state
  feed: [],
  myStories: [],
  isLoading: false,
  isPosting: false,
  uploadProgress: 0,
  error: null,

  // Fetch story feed (friends' stories)
  fetchFeed: async () => {
    set({ isLoading: true, error: null });

    const response = await api.get<UserStoryGroup[]>('/stories/feed');

    if (response.success && response.data) {
      set({ feed: response.data, isLoading: false });
    } else {
      // Silently fail - stories are not critical
      set({ feed: [], isLoading: false });
    }
  },

  // Fetch my own stories
  fetchMyStories: async () => {
    set({ isLoading: true, error: null });

    const response = await api.get<Story[]>('/stories/mine');

    if (response.success && response.data) {
      set({ myStories: response.data, isLoading: false });
    } else {
      // Silently fail - stories are not critical
      set({ myStories: [], isLoading: false });
    }
  },

  // Fetch stories from a specific user
  fetchUserStories: async (userId: number): Promise<Story[]> => {
    const response = await api.get<Story[]>(`/stories/user/${userId}`);

    if (response.success && response.data) {
      return response.data;
    }

    return [];
  },

  // Record that a story was actually DISPLAYED. Fire-and-forget: the viewer
  // must never stall on this, and the DB dedupes repeats. Media fetches
  // deliberately don't count (the viewer preloads ahead) — this explicit
  // call is the only thing that marks a story seen.
  recordView: (storyId: number, ownerUserId: number) => {
    void api.get(`/stories/${storyId}?record_view=true`);
    get().markStoryViewed(storyId, ownerUserId);
  },

  // Get story viewers (owner only)
  getStoryViewers: async (storyId: number): Promise<StoryViewer[]> => {
    const response = await api.get<StoryViewer[]>(`/stories/${storyId}/viewers`);

    if (response.success && response.data) {
      return response.data;
    }

    return [];
  },

  // Get story reactions
  getStoryReactions: async (storyId: number): Promise<StoryReaction[]> => {
    const response = await api.get<StoryReaction[]>(`/stories/${storyId}/reactions`);

    if (response.success && response.data) {
      return response.data;
    }

    return [];
  },

  // Post a new story
  postStory: async (data: PostStoryData): Promise<boolean> => {
    set({ isPosting: true, uploadProgress: 0, error: null });

    const formData = new FormData();
    // BUG-115: a Memory the server already holds is copied by id instead of
    // round-tripping its bytes. Everything the CAMERA posts goes through the
    // send queue now (FEAT-038 wave 2); this path is the viewer's "add to my
    // story", which has the bytes in hand and no draft behind it.
    if (data.saved_item_id) {
      formData.append('saved_item_id', String(data.saved_item_id));
    } else {
      formData.append('media', data.media);
    }
    formData.append('media_type', data.media_type);
    const overlaysJson = serializeOverlays(data.overlays);
    if (overlaysJson) formData.append('overlays', overlaysJson);
    if (data.hd) {
      formData.append('hd', 'true');
    }

    // Multi-shot batches group and order by these; position 0 is valid
    if (data.batch_id) {
      formData.append('batch_id', data.batch_id);
      formData.append('batch_position', String(data.batch_position ?? 0));
    }

    if (data.caption) {
      formData.append('caption', data.caption);
    }

    formData.append('audience', data.audience ?? 'everyone');

    if (data.private_viewer_ids && data.private_viewer_ids.length > 0) {
      formData.append('private_viewer_ids', data.private_viewer_ids.join(','));
    }

    // BUG-055: idempotency key — a retried post converges on one story
    if (data.client_key) {
      formData.append('client_key', data.client_key);
    }
    // FEAT-024: only ever sent when it was actually observed
    if (data.facing) {
      formData.append('facing', data.facing);
    }

    const response = await api.upload<Story>(
      '/stories',
      formData,
      (progress) => set({ uploadProgress: progress }),
      { timeoutMs: computeUploadTimeoutMs(data.media.size) }
    );

    if (response.success && response.data) {
      // Add to my stories
      set((state) => ({
        myStories: [response.data as Story, ...state.myStories],
        isPosting: false,
        uploadProgress: 100,
      }));

      // Refresh feed
      get().fetchFeed();

      return true;
    }

    set({
      isPosting: false,
      error: response.error?.message || 'Failed to post story',
    });
    return false;
  },

  // Delete a story
  deleteStory: async (storyId: number): Promise<boolean> => {
    const response = await api.delete<{ deleted: boolean }>(`/stories/${storyId}`);

    if (response.success) {
      // Remove from my stories
      set((state) => ({
        myStories: state.myStories.filter((s) => s.id !== storyId),
      }));

      return true;
    }

    set({ error: response.error?.message || 'Failed to delete story' });
    return false;
  },

  // React to a story
  reactToStory: async (storyId: number, emoji: string): Promise<boolean> => {
    const response = await api.post<StoryReaction>(`/stories/${storyId}/reactions`, { emoji });

    if (response.success) {
      return true;
    }

    set({ error: response.error?.message || 'Failed to react' });
    return false;
  },

  // Remove reaction from a story
  removeReaction: async (storyId: number): Promise<boolean> => {
    const response = await api.delete<{ removed: boolean }>(`/stories/${storyId}/reactions`);
    return response.success;
  },

  // Reply to a story (sends as DM)
  replyToStory: async (storyId: number, content: string): Promise<boolean> => {
    const response = await api.post<{ replied: boolean }>(`/stories/${storyId}/reply`, { content });

    if (response.success) {
      return true;
    }

    set({ error: response.error?.message || 'Failed to reply' });
    return false;
  },

  // Save story to vault
  saveToVault: async (storyId: number): Promise<boolean> => {
    const response = await api.post<{ saved: boolean }>(`/stories/${storyId}/save`);

    if (response.success) {
      return true;
    }

    set({ error: response.error?.message || 'Failed to save to vault' });
    return false;
  },

  // Mark a story as viewed in the local state
  markStoryViewed: (storyId: number, userId: number) => {
    set((state) => {
      // Update feed
      const newFeed = state.feed.map((group) => {
        if (group.user.id !== userId) return group;

        const newStories = group.stories.map((story) => {
          if (story.id !== storyId) return story;
          return { ...story, viewed: true };
        });

        const hasUnwatched = newStories.some((s) => !s.viewed);

        return {
          ...group,
          stories: newStories,
          has_unwatched: hasUnwatched,
        };
      });

      return { feed: newFeed };
    });
  },

  // Clear error
  clearError: () => set({ error: null }),
}));
